import React from 'react';
import { Card, Row, Col, Typography, Statistic } from 'antd';
import styled from 'styled-components';

const { Text } = Typography;

interface StatItem {
  title: string;
  value: string | number;
  suffix?: string;
  prefix?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color?: string;
}

interface StatsCardProps {
  title?: string;
  stats: StatItem[];
  loading?: boolean;
  className?: string;
  columns?: 1 | 2 | 3 | 4;
}

const StyledCard = styled(Card)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  .ant-card-head {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 20px 24px 16px;
    min-height: auto;
  }

  .ant-card-head-title {
    padding: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .ant-card-body {
    padding: 24px;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-statistic-title {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 8px;
    font-weight: 500;
  }

  .ant-statistic-content {
    font-size: 24px;
    font-weight: 700;
    color: #1a1a2e;
    
    .ant-statistic-content-value {
      color: inherit;
    }
  }
`;

const TrendIndicator = styled.div<{ trend: 'up' | 'down' | 'neutral' }>`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => 
    props.trend === 'up' ? '#52c41a' :
    props.trend === 'down' ? '#ff4d4f' :
    'rgba(0, 0, 0, 0.45)'
  };
  
  &::before {
    content: ${props => 
      props.trend === 'up' ? '"↗ "' :
      props.trend === 'down' ? '"↘ "' :
      '""'
    };
  }
`;

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  stats,
  loading = false,
  className = '',
  columns = 3
}) => {
  const getColSpan = () => {
    switch (columns) {
      case 1: return 24;
      case 2: return 12;
      case 3: return 8;
      case 4: return 6;
      default: return 8;
    }
  };

  return (
    <StyledCard
      title={title}
      loading={loading}
      className={className}
      bordered={false}
    >
      <Row gutter={[16, 16]}>
        {stats.map((stat, index) => (
          <Col key={index} xs={24} sm={12} lg={getColSpan()}>
            <StatItem>
              <Statistic
                title={stat.title}
                value={stat.value}
                suffix={stat.suffix}
                prefix={stat.prefix}
                valueStyle={{ 
                  color: stat.color || '#1a1a2e',
                  fontSize: '24px',
                  fontWeight: 700
                }}
              />
              {stat.trend && stat.trendValue && (
                <TrendIndicator trend={stat.trend}>
                  {stat.trendValue}
                </TrendIndicator>
              )}
            </StatItem>
          </Col>
        ))}
      </Row>
    </StyledCard>
  );
};







